import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Baie coulissante', to: '/reparation-baie-coulissante-herault/' },
  { label: 'Rail de baie' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Réparation et remplacement de rail de baie coulissante dans l\'Hérault',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'AdministrativeArea', name: 'Hérault' },
  description: 'Rail de baie coulissante encrassé, déformé ou endommagé dans l\'Hérault ? Nettoyage, redressement et remplacement par nos techniciens.',
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Peut-on réparer un rail de baie coulissante sans changer toute la baie ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Dans la majorité des cas, oui. Un rail encrassé peut être nettoyé en profondeur, un rail légèrement déformé peut être redressé, et un rail usé localement peut être remplacé partiellement. Le remplacement complet du rail est une solution de dernier recours, lorsque la déformation ou l\'usure est trop importante.',
      },
    },
    {
      '@type': 'Question',
      name: 'Quels sont les signes d\'un rail de baie coulissante endommagé ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Les signes incluent un bruit de raclement au coulissement, une résistance anormale lors de la manœuvre, des à-coups dans le mouvement, ou des traces d\'usure visibles sur la surface du rail. Si votre baie a tendance à dérailler fréquemment, le rail est probablement en cause.',
      },
    },
    {
      '@type': 'Question',
      name: 'L\'air salin du littoral héraultais peut-il abîmer le rail ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, l\'air salin peut accélérer la corrosion des rails en aluminium, en particulier dans les zones côtières comme Sète, Agde ou La Grande-Motte. Un entretien régulier et un nettoyage du rail permettent de limiter ce phénomène. En cas de corrosion avancée, nous pouvons remplacer le rail affecté.',
      },
    },
  ],
};

const contentBlocks = [
  {
    image: '/images/rail-block1.jpg',
    imageAlt: 'Rail de baie coulissante encrasse et use dans l Herault',
    children: (
      <>
        <h2>Le rail, fondation du coulissement</h2>
        <p>
          Le rail de coulissement est l'élément sur lequel repose toute la mécanique de votre baie vitrée. C'est lui qui guide les roulettes et supporte le poids du vantail — parfois plus de 200 kilos pour les grandes baies aluminium contemporaines. Un rail en bon état est la condition sine qua non d'un coulissement fluide, silencieux et sécurisé. Dans l'Hérault, les conditions climatiques — poussières, sable des plages de Palavas ou de La Grande-Motte, air salin du littoral — peuvent accélérer l'encrassement et la dégradation des rails de baie coulissante.
        </p>
        <h3>Les trois types d'intervention sur le rail</h3>
        <h4>Nettoyage approfondi du rail</h4>
        <p>
          La cause la plus fréquente d'un coulissement difficile est l'encrassement du rail. Poussières, sable, débris végétaux, résidus de lubrifiant dégradé : tout cela s'accumule dans le chemin de roulement et freine le mouvement des galets. Nos techniciens effectuent un nettoyage mécanique complet du rail, suivi d'une lubrification adaptée aux menuiseries aluminium. Cette opération simple restaure souvent un coulissement impeccable sans nécessiter de remplacement de pièces.
        </p>
      </>
    ),
  },
  {
    image: '/images/rail-block2.webp',
    imageAlt: 'Technicien DFPRO reparant le rail de baie coulissante',
    imagePosition: 'right' as const,
    children: (
      <>
        <h4>Redressement du rail</h4>
        <p>
          Un rail peut se déformer sous l'effet de plusieurs facteurs : choc accidentel, tassement différentiel du bâti, ou simplement le poids constant du vantail sur une zone localisée. Une déformation, même minime, suffit à perturber le coulissement et à user prématurément les roulettes. Nos techniciens évaluent la gravité de la déformation et, lorsque c'est possible, procèdent à un redressement du rail avec des outils de précision, sans avoir à le remplacer.
        </p>
        <h4>Remplacement du rail</h4>
        <p>
          Lorsque le rail est trop endommagé — corrosion avancée, fissure, usure profonde du chemin de roulement — le remplacement devient nécessaire. Nous commandons un rail compatible avec votre menuiserie et procédons à son installation dans les règles de l'art. Le nouveau rail est parfaitement aligné et fixé pour garantir un coulissement optimal et durable.
        </p>
        <h3>Entretien préventif : les bons gestes</h3>
        <p>
          Pour préserver votre rail de baie coulissante, un entretien régulier est recommandé : aspirer ou brosser le rail tous les deux à trois mois pour éliminer les poussières et débris, vérifier l'absence de déformation, et appliquer un lubrifiant sec adapté. Dans les zones côtières de l'Hérault comme Sète, Agde ou Marseillan, une attention particulière doit être portée à la corrosion. Si vous constatez une résistance anormale au coulissement, n'attendez pas : nos techniciens peuvent diagnostiquer et traiter le problème avant qu'il ne s'aggrave.
        </p>
        <p>
          Le rail est indissociable des roulettes et galets. Si vous rencontrez des difficultés de coulissement, consultez également notre page sur le <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">remplacement des roulettes et galets</Link>. Pour une vue d'ensemble de nos prestations, rendez-vous sur notre page <Link to="/reparation-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réparation de baie coulissante dans l'Hérault</Link>.
        </p>
      </>
    ),
  },
];

export default function RailBaie() {
  return (
    <>
      <SEO
        title="Réparation de rail de baie coulissante Hérault | DFPRO"
        description="Rail de baie coulissante encrassé ou déformé dans l'Hérault ? Nettoyage, redressement, remplacement. Devis gratuit, garantie 10 ans. 09 80 80 22 42."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/reparation-rail-baie-coulissante-herault/"
      />
      <ServicePageLayout
        title="Réparation et remplacement de rail de baie coulissante dans l'Hérault"
        subtitle="Rail encrassé, déformé ou corrodé ? Nos techniciens nettoient, redressent ou remplacent votre rail de baie coulissante dans tout l'Hérault."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        heroImage="/images/rail-hero.jpg"
        heroImageAlt="Rail de baie coulissante aluminium DFPRO Herault"
        contentBlocks={contentBlocks}
      />
    </>
  );
}